import React, { useEffect, useState } from "react";
import { useAdminAuth } from "../context/AdminAuthContext";
import ApprovalModal from "../componants/ApprovalModal";
import { 
  Loader2, UserCheck, ShieldAlert, CheckCircle, 
  Mail, Calendar, ArrowRight, User 
} from "lucide-react";

const Approvals = () => {
  const { token } = useAdminAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const fetchPending = async () => {
    try {
      const res = await fetch("/springboot/api/admin/team/pending", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if(res.ok) setUsers(await res.json());
    } catch(err) { console.error(err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPending(); }, []);

  return (
    // Clean White Background
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-7xl mx-auto px-6 py-12">
        
        {/* Simple Header */}
        <div className="mb-12 border-b border-gray-100 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black text-gray-900 tracking-tight">Pending Approvals</h1>
            <p className="text-gray-500 font-medium mt-2 text-lg">Review and assign roles to new team requests.</p>
          </div>
          
          {/* Quick Stat */}
          {!loading && users.length > 0 && (
             <div className="px-5 py-2 bg-orange-50 text-orange-600 rounded-full font-bold text-xs uppercase tracking-widest border border-orange-100">
                {users.length} Request{users.length > 1 ? 's' : ''} Pending
             </div>
          )}
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-40">
             <Loader2 className="animate-spin text-[#006d5b] mb-4" size={40} />
             <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Fetching Requests...</p>
          </div>
        ) : users.length === 0 ? (
          // Empty State (Clean)
          <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-gray-100 rounded-[30px] bg-gray-50/30">
             <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-gray-300">
                <UserCheck size={40} />
             </div>
             <h3 className="text-xl font-black text-gray-900">All Caught Up!</h3>
             <p className="text-gray-400 text-sm font-medium mt-2">There are no pending team requests at the moment.</p>
          </div>
        ) : (
          <>
            {/* === DESKTOP VIEW (Clean Table) === */}
            <div className="hidden md:block border border-gray-200 rounded-3xl overflow-hidden shadow-sm bg-white">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 border-b border-gray-100 text-[11px] uppercase tracking-widest text-gray-500 font-black">
                  <tr>
                    <th className="p-6 pl-8">Candidate Name</th>
                    <th className="p-6">Email Address</th>
                    <th className="p-6">Request Date</th>
                    <th className="p-6 text-right pr-8">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm">
                  {users.map((user: any) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                      <td className="p-6 pl-8">
                         <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-[#006d5b]/10 flex items-center justify-center font-black text-[#006d5b] text-sm uppercase">
                               {user.name.charAt(0)}
                            </div>
                            <span className="font-bold text-gray-900 text-base">{user.name}</span>
                         </div>
                      </td>
                      <td className="p-6 text-gray-500 font-medium">
                         <div className="flex items-center gap-2">
                            <Mail size={16} className="text-gray-300"/> {user.email}
                         </div>
                      </td>
                      <td className="p-6 text-gray-500 font-medium">
                         <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-gray-300"/> {new Date().toLocaleDateString()}
                         </div>
                      </td>
                      <td className="p-6 text-right pr-8">
                        <button 
                          onClick={() => setSelectedUser(user)}
                          className="bg-white border-2 border-gray-100 text-gray-600 hover:border-[#006d5b] hover:text-[#006d5b] px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 ml-auto group-hover:shadow-md"
                        >
                          Review <ArrowRight size={14}/>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* === MOBILE VIEW (Clean Cards) === */}
            <div className="md:hidden grid grid-cols-1 gap-6">
               {users.map((user: any) => (
                  <div key={user.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
                     <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                           <div className="w-14 h-14 rounded-2xl bg-[#006d5b] text-white flex items-center justify-center font-black text-xl uppercase shadow-lg shadow-[#006d5b]/20">
                              {user.name.charAt(0)}
                           </div>
                           <div>
                              <h3 className="text-xl font-black text-gray-900 leading-tight">{user.name}</h3>
                              <p className="text-xs text-gray-400 font-bold mt-1">Pending Request</p>
                           </div>
                        </div>
                     </div>
                     
                     <div className="space-y-4 mb-8">
                        <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
                           <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400"><Mail size={16}/></div>
                           {user.email}
                        </div>
                        <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
                           <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400"><Calendar size={16}/></div>
                           {new Date().toLocaleDateString()}
                        </div>
                     </div>

                     <button 
                        onClick={() => setSelectedUser(user)}
                        className="w-full bg-[#006d5b] hover:bg-[#005a4b] text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg shadow-[#006d5b]/20 flex items-center justify-center gap-2 active:scale-95 transition-all"
                     >
                        Review Application <CheckCircle size={18}/>
                     </button>
                  </div>
               ))}
            </div>
          </>
        )}

        {selectedUser && (
          <ApprovalModal 
              user={selectedUser} 
              onClose={() => setSelectedUser(null)} 
              onSuccess={fetchPending} 
          />
        )}
      </div>
    </div>
  );
};

export default Approvals;